import React, { useCallback, useEffect, useState } from 'react'
import { Card } from 'primereact/card'
import { FiPlusCircle } from 'react-icons/fi'
import { HiOutlineDocumentReport } from 'react-icons/hi'
import { FaCheckDouble, FaTrash } from 'react-icons/fa'
import toast from 'react-hot-toast'
import { Form, Formik } from 'formik'
import * as Yup from 'yup'

import { fetchData } from '../../../helpers/fetchData'
import { labModuleApi } from '../../../helpers/labModuleApi'
import FormListItem from './FormListItem'
import SkeletonCardLoading from '../../../shared/SkeltonCardLoading'
import NoDataFound from '../../../shared/NoDataFound'
import DropdownField from '../../../shared/DropdownField'
import TextInput from '../../../shared/TextInput'
import CustomButton from '../../../shared/CustomButton'
import { formListCategoriesTypes } from '../../../constants/FormCreationConstants'
import { t } from 'i18next'
import CheckPermission from '../../../hooks/CheckPermission'
const ReusableModal = React.lazy(() => import('../../../shared/ReuseableModal'))
const ConfirmationModal = React.lazy(() =>
  import('../../../shared/ConfirmationModal')
)

const FormCategoriesList = ({
  selectedFormId,
  setSelectedCategoryId,
  selectedFormType,
  setSelectedElementFormId,
  isAllowActions
}) => {
  const { hasPermission } = CheckPermission()
  const [isLoading, setIsLoading] = useState(false)
  const [buttonLoading, setButtonLoading] = useState(false)
  const [categories, setCategories] = useState([])
  const [selectedCategory, setSelectedCategory] = useState(null)
  const [openConfirmationModal, setOpenConfirmationModal] = useState(false)
  const [openCreateCategoryModal, setOpenCreateCategoryModal] = useState(false)
  const [activeItemId, setActiveItemId] = useState(null)

  const fetchCategories = useCallback(async () => {
    setIsLoading(true)
    const response = await fetchData(
      'GET',
      labModuleApi.categories.getAllByFormId(selectedFormId)
    )
    let categoriesData = response?.data ? response?.data : []
    setIsLoading(false)
    setCategories(categoriesData)
  }, [selectedFormId, selectedFormType])

  useEffect(() => {
    fetchCategories()
  }, [fetchCategories, selectedFormId, selectedFormType])

  const handleCategoryItemClick = data => {
    setActiveItemId(data?.id)
    setSelectedCategoryId(data?.id)
    if (setSelectedElementFormId) {
      setSelectedElementFormId(data?.form_id)
    }
  }

  const handleAddNewCategory = () => {
    setSelectedCategory(null)
    setOpenCreateCategoryModal(true)
  }

  const handleEditCategory = formData => {
    setSelectedCategory(formData)
    setOpenCreateCategoryModal(true)
  }
  const hanldeDeleteCategory = formData => {
    setSelectedCategory(formData)
    setOpenConfirmationModal(true)
  }

  const handleConfirmDeleteCategory = async () => {
    if (!hasPermission('forms-creation', 'delete')) return
    setButtonLoading(true)
    let response = await fetchData(
      'DELETE',
      labModuleApi.categories.remove(selectedCategory?.id)
    )
    if (response) {
      toast.success(response?.message)
      setSelectedCategory(null)
      setOpenConfirmationModal(false)
      fetchCategories()
    }
    setButtonLoading(false)
  }

  return (
    <Card
      pt={{
        root: { className: 'border border-gray-100 shadow px-0 h-fit' },
        body: { className: 'px-0' }
      }}
    >
      <div className='flex items-center justify-between px-3 border-b border-gray-300 pb-3'>
        <h6 className='text-20-gray !font-semibold'>{t('Categories')}</h6>
        {isAllowActions && hasPermission('forms-creation', 'create') && (
          <div>
            <FiPlusCircle
              size={27}
              className='text-blue cursor-pointer'
              onClick={handleAddNewCategory}
            />
          </div>
        )}
      </div>
      {isLoading ? (
        <SkeletonCardLoading count={10} />
      ) : !categories?.length ? (
        <div className='my-4 lg:my-8'>
          <NoDataFound
            iconSize='30'
            titleClassName='text-16-dark font-semibold'
          />
        </div>
      ) : (
        <div className='form-creation-list-menu'>
          {categories?.map((data, index) => (
            <div
              key={index}
              className={`py-3 px-3 border-b border-b-gray-100 ${
                activeItemId == data?.id ? 'bg-gray-200' : ''
              }`}
            >
              <FormListItem
                isAllowActions={isAllowActions}
                data={data}
                onEditClick={handleEditCategory}
                onDeleteClick={hanldeDeleteCategory}
                onContainerClick={handleCategoryItemClick}
              />
            </div>
          ))}
        </div>
      )}
      <ReusableModal
        isVisible={openCreateCategoryModal}
        position='top'
        title={
          selectedCategory ? t('Update Category') : t('Create New Category')
        }
        HeaderIcon={HiOutlineDocumentReport}
        onClose={() => {
          setOpenCreateCategoryModal(false)
          setSelectedCategory(null)
        }}
      >
        <CreateCategoryForm
          selectedCategory={selectedCategory}
          selectedFormType={selectedFormType}
          fetchCategories={fetchCategories}
          setOpenCreateCategoryModal={setOpenCreateCategoryModal}
          setSelectedCategory={setSelectedCategory}
          selectedFormId={selectedFormId}
        />
      </ReusableModal>
      <ConfirmationModal
        disabled={buttonLoading}
        isLoading={buttonLoading}
        visible={openConfirmationModal}
        setVisible={setOpenConfirmationModal}
        header={'Confirm Delete'}
        acceptLabel={'Delete'}
        rejectLabel={'Cancel'}
        Icon={FaTrash}
        onAccept={() => handleConfirmDeleteCategory()}
        onReject={() => setSelectedCategory(null)}
      />
    </Card>
  )
}

const CreateCategoryForm = ({
  selectedCategory,
  selectedFormType,
  fetchCategories,
  setOpenCreateCategoryModal,
  setSelectedCategory,
  selectedFormId
}) => {
  const { hasPermission } = CheckPermission()
  const [isLoading, setIsLoading] = useState(false)

  const initialValues = {
    form_id: selectedFormId || '',
    type: selectedCategory?.type || '',
    name_en: selectedCategory?.name_en || '',
    name_ar: selectedCategory?.name_ar || '',
    request_type: selectedFormType || 'visit'
  }

  const validationSchema = Yup.object().shape({
    type: Yup.string().trim().required(t('Type is required')),
    name_en: Yup.string().trim().required(t('Report Name (EN) is required')),
    name_ar: Yup.string().trim().required(t('Report Name (AR) is required'))
  })

  const action = selectedCategory ? 'update' : 'create'

  const handleSubmit = async values => {
    if (!hasPermission('forms-creation', action)) return
    selectedCategory
      ? handleUpdateCategory(values)
      : handleCreateCategory(values)
  }

  const handleCreateCategory = async payload => {
    setIsLoading(true)
    let response = await fetchData(
      'POST',
      labModuleApi.categories.create(),
      payload
    )
    setIsLoading(false)
    if (response) {
      toast.success(response?.message)
      fetchCategories()
      setOpenCreateCategoryModal(false)
      setSelectedCategory(null)
    }
  }

  const handleUpdateCategory = async payload => {
    setIsLoading(true)
    let response = await fetchData(
      'PUT',
      labModuleApi.categories.update(selectedCategory?.id),
      payload
    )
    setIsLoading(false)
    if (response) {
      toast.success(response?.message)
      fetchCategories()
      setOpenCreateCategoryModal(false)
      setSelectedCategory(null)
    }
  }

  return (
    <div>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {props => (
          <Form>
            <div className='grid grid-cols-2 gap-2 mt-4'>
              <DropdownField
                name='type'
                label={t('Select Type*')}
                options={formListCategoriesTypes}
                optionLabel='label'
                optionValue='value'
                placeholder={t('Select type')}
              />
              <TextInput
                name='name_en'
                label={t('Category Name (EN)*')}
                placeholder={t('Enter category name en')}
              />
              <TextInput
                name='name_ar'
                label={t('Category Name (AR)')}
                placeholder={t('Enter category name ar')}
              />
            </div>
            {hasPermission('forms-creation', action) && (
              <div className='flex items-center justify-end mt-2'>
                <CustomButton
                  disabled={isLoading}
                  loading={isLoading}
                  type='submit'
                  label={
                    selectedCategory ? t('Update Category') : t('Add Category')
                  }
                  buttonType='primary'
                  icon={<FaCheckDouble size={12} className='me-2' />}
                />
              </div>
            )}
          </Form>
        )}
      </Formik>
    </div>
  )
}

export default FormCategoriesList
